import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Shield, Mail, Lock, Loader2, AlertCircle, CheckCircle2, Clock, Info } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const STATUS_BANNERS = {
  verify_email: {
    icon: Info,
    color: 'bg-blue-50 border-blue-200 text-blue-800',
    message: 'Un e-mail de vérification vous a été envoyé. Cliquez sur le lien pour continuer.'
  },
  pending_approval: {
    icon: Clock,
    color: 'bg-amber-50 border-amber-200 text-amber-800',
    message: 'Votre e-mail a été vérifié. Votre demande d\'accès est en cours de traitement par un administrateur.'
  },
  auto_approved: {
    icon: CheckCircle2,
    color: 'bg-green-50 border-green-200 text-green-800',
    message: 'Votre accès a été approuvé. Vous pouvez vous connecter avec Microsoft.'
  }
};

const ERROR_BANNERS = {
  domain_not_allowed: 'Votre domaine e-mail n\'est pas autorisé. Contactez votre administrateur.',
  request_rejected: 'Votre demande d\'accès a été refusée.',
  request_pending: 'Votre demande d\'accès est déjà en cours de traitement.',
  microsoft_not_configured: 'L\'authentification Microsoft n\'est pas encore configurée.',
  microsoft_disabled: 'L\'authentification Microsoft est désactivée.',
  account_disabled: 'Votre compte est désactivé. Contactez votre administrateur.',
  microsoft_error: 'Une erreur est survenue lors de l\'authentification Microsoft.',
  no_email: 'Impossible de récupérer votre e-mail depuis Microsoft.',
  invalid_token: 'Le lien de vérification est invalide ou expiré.',
  request_not_found: 'Demande d\'accès non trouvée.',
  verification_error: 'Erreur lors de la vérification de votre e-mail.',
  microsoft_api_error: 'Erreur de communication avec Microsoft. Réessayez.'
};

const AdminLogin = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  // 2FA (comptes locaux)
  const [twoFA, setTwoFA] = useState({ active: false, challenge: '', channel: '', masked: '', code: '' });
  // Politique SSO Microsoft obligatoire pour le staff
  const [policy, setPolicy] = useState({ staffSsoEnforced: false, microsoftEnabled: true });
  const [showLocalForm, setShowLocalForm] = useState(false);
  const [ssoError, setSsoError] = useState('');

  const statusParam = searchParams.get('status');
  const errorParam = searchParams.get('error');
  const microsoftToken = searchParams.get('microsoft_auth');

  useEffect(() => {
    if (microsoftToken) {
      handleMicrosoftTokenCallback();
    }
  }, [microsoftToken]);

  useEffect(() => {
    axios.get(`${API}/auth/login-policy`)
      .then(res => setPolicy(res.data || { staffSsoEnforced: false, microsoftEnabled: true }))
      .catch(() => {});
  }, []);

  const handleMicrosoftTokenCallback = () => {
    try {
      const userJson = searchParams.get('microsoft_user');
      if (!userJson) return;

      const user = JSON.parse(decodeURIComponent(userJson));

      // SEC-001 (20/10/2026) — aucun jeton ne transite par l'URL ni par localStorage :
      // la session repose uniquement sur les cookies HttpOnly posés par le backend
      // (`baobab_session` + `baobab_refresh`, lus en priorité par /auth/refresh-token).
      localStorage.setItem('baobab_user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = 'Bearer cookie-auth';
      updateUser(user);

      toast({
        title: 'Connexion Microsoft réussie',
        description: `Bienvenue ${user.firstName || ''}`
      });

      setSearchParams({});
      window.location.href = '/admin/dashboard';
    } catch (err) {
      console.error('Microsoft token callback error:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de finaliser la connexion Microsoft',
        variant: 'destructive'
      });
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const completeLogin = (data) => {
    const { token, refreshToken: newRefreshToken, expiresIn, user } = data;
    const allowedRoles = [
      'admin', 'lead_ops', 'ops', 'operateur', 'lead_field_ops', 'field_ops',
      'lead_commercial', 'commercial', 'finance', 'auditeur', 'comptable',
    ];
    if (!allowedRoles.includes(user.role)) {
      toast({ title: 'Acces refuse', description: "Vous n'avez pas les droits d'administration", variant: 'destructive' });
      return;
    }
    // SEC-06 — auth via cookie HttpOnly : ne pas stocker le JWT en localStorage (vecteur XSS)
    localStorage.setItem('baobab_user', JSON.stringify(user));
    if (newRefreshToken) localStorage.setItem('baobab_refresh_token', newRefreshToken);
    if (expiresIn) localStorage.setItem('baobab_token_expiry', (Date.now() + expiresIn * 1000).toString());
    axios.defaults.headers.common['Authorization'] = 'Bearer cookie-auth';
    updateUser(user);
    window.location.href = '/admin/dashboard';
    toast({ title: 'Connexion réussie', description: `Bienvenue dans l'espace ${user.role === 'admin' ? 'administrateur' : 'staff'}` });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/admin/login`, {
        email: formData.email,
        password: formData.password
      });

      if (response.status === 200) {
        // 2FA requise (compte local) → afficher l'étape OTP
        if (response.data?.requires2FA) {
          setTwoFA({
            active: true, challenge: response.data.challenge,
            channel: response.data.channel, masked: response.data.masked, code: ''
          });
          toast({ title: 'Vérification requise', description: `Un code a été envoyé (${response.data.channel === 'whatsapp' ? 'WhatsApp' : 'Email'}).` });
          return;
        }
        completeLogin(response.data);
      }
    } catch (error) {
      console.error('Admin login error:', error);
      const detail = error.response?.data?.detail;
      const detailObj = error.response?.data?._detailObject;
      if (error.response?.status === 403 && detailObj?.code === 'SSO_REQUIRED') {
        setSsoError(detailObj.message);
        setShowLocalForm(false);
        setFormData(prev => ({ ...prev, password: '' }));
        return;
      }
      toast({
        title: 'Erreur de connexion',
        description: (typeof detail === 'string' ? detail : detail?.message) || 'Email ou mot de passe incorrect',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/admin/2fa/verify`, {
        challenge: twoFA.challenge, code: (twoFA.code || '').replace(/\D/g, '')
      });
      if (response.status === 200) completeLogin(response.data);
    } catch (error) {
      toast({
        title: 'Code invalide',
        description: error.response?.data?.detail || 'Le code est incorrect ou expiré',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = () => {
    setMicrosoftLoading(true);
    window.location.href = `${API}/auth/microsoft/login`;
  };

  const microsoftButton = (
    <Button
      type="button"
      variant="outline"
      className="w-full border-gray-300 hover:bg-gray-50 text-gray-700 font-medium"
      onClick={handleMicrosoftLogin}
      disabled={microsoftLoading}
      data-testid="microsoft-login-btn"
    >
      {microsoftLoading ? (
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      ) : (
        <span className="mr-2"><MicrosoftIcon /></span>
      )}
      Se connecter avec Microsoft
    </Button>
  );

  const bannerStatus = statusParam && STATUS_BANNERS[statusParam];
  const bannerError = errorParam && ERROR_BANNERS[errorParam];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-gray-700" data-testid="admin-login-card">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-gradient-to-br from-red-600 to-red-700 p-3 rounded-xl shadow-md">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold">Baobab Connect</CardTitle>
          <CardDescription>Connexion</CardDescription>
        </CardHeader>
        <CardContent>
          {bannerStatus && (
            <div className={`flex items-start gap-3 p-3 rounded-lg border mb-4 ${bannerStatus.color}`} data-testid="status-banner">
              <bannerStatus.icon className="w-5 h-5 mt-0.5 shrink-0" />
              <p className="text-sm">{bannerStatus.message}</p>
            </div>
          )}

          {bannerError && (
            <div className="flex items-start gap-3 p-3 rounded-lg border mb-4 bg-red-50 border-red-200 text-red-800" data-testid="error-banner">
              <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
              <p className="text-sm">{bannerError}</p>
            </div>
          )}

          {ssoError && (
            <div className="flex items-start gap-3 p-3 rounded-lg border mb-4 bg-red-50 border-red-200 text-red-800" data-testid="sso-required-banner">
              <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
              <p className="text-sm">{ssoError}</p>
            </div>
          )}

          {policy.staffSsoEnforced && !twoFA.active && (
            <div className="mb-5 space-y-3" data-testid="sso-enforced-block">
              {microsoftButton}
              <div className="relative my-1">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">ou</span>
                </div>
              </div>
            </div>
          )}

          {twoFA.active ? (
            <form onSubmit={handleVerify2FA} className="space-y-4" data-testid="admin-2fa-form">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
                Un code de vérification a été envoyé {twoFA.masked ? `à ${twoFA.masked}` : ''} via {twoFA.channel === 'whatsapp' ? 'WhatsApp' : (twoFA.channel === 'email' ? 'Email' : 'le canal disponible')}.
              </div>
              <div className="space-y-2">
                <Label htmlFor="otp2fa">Code de vérification (6 chiffres)</Label>
                <Input
                  id="otp2fa"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={twoFA.code}
                  onChange={(e) => setTwoFA({ ...twoFA, code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  className="text-center text-2xl tracking-[0.5em] font-semibold focus:border-red-600 focus:ring-red-600"
                  required
                  data-testid="admin-2fa-code-input"
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
                disabled={isLoading}
                data-testid="admin-2fa-submit"
              >
                {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Vérification...</> : <><Shield className="mr-2 h-4 w-4" />Vérifier et se connecter</>}
              </Button>
              <button type="button" onClick={() => setTwoFA({ active: false, challenge: '', channel: '', masked: '', code: '' })} className="w-full text-xs text-gray-500 hover:underline" data-testid="admin-2fa-back">
                ← Revenir à la connexion
              </button>
            </form>
          ) : (policy.staffSsoEnforced && !showLocalForm) ? (
            <button
              type="button"
              onClick={() => setShowLocalForm(true)}
              className="w-full text-xs text-gray-500 hover:text-gray-700 hover:underline py-2"
              data-testid="toggle-local-login-btn"
            >
              Autre méthode de connexion
            </button>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="votre.email@exemple.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10 focus:border-red-600 focus:ring-red-600"
                  required
                  data-testid="admin-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Mot de passe</Label>
                <Link to="/forgot-password" className="text-xs text-red-600 hover:text-red-700 hover:underline">
                  Mot de passe oublie ?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="????????"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 focus:border-red-600 focus:ring-red-600"
                  required
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-md hover:shadow-lg transition-all"
              disabled={isLoading}
              data-testid="admin-login-submit"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connexion...
                </>
              ) : (
                <>
                  <Shield className="mr-2 h-4 w-4" />
                  Se connecter
                </>
              )}
            </Button>
          </form>
          )}

          {!policy.staffSsoEnforced && (
            <>
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">ou</span>
                </div>
              </div>
              {microsoftButton}
            </>
          )}

        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
